import React, { useEffect, useState } from "react";
import { Button, Card, Col, Row, Spinner, Alert, Table, Form, Badge, ProgressBar, Dropdown, Modal } from "react-bootstrap";
import ReactApexChart from "react-apexcharts";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";
import { formatCurrency, formatNumber, nettoyerMontant } from "../utils/formatters";
import './ChiffreAffaires.css';
import { exportCAtoExcel, exportCAtoCSV } from "../utils/excelExport";
import { generateCAPDF } from "../utils/pdfExport";
import { API_BASE_URL } from "../constants/api";
import logo from "../assets/img/IMG-20250130-WA0008.jpg";

export default function ChiffreAffaires() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [viewMode, setViewMode] = useState('mensuel');
  const [chartLoading, setChartLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showPdfModal, setShowPdfModal] = useState(false);

  useEffect(() => {
    fetchChiffreAffaires();
  }, [selectedYear, selectedMonth]);

  const handleExportPDF = async () => {
    setPdfLoading(true);
    setShowPdfModal(true);
    try {
      await generateCAPDF(data, selectedYear);
    } catch (error) {
      console.error("Erreur PDF:", error);
      alert("Erreur lors de la génération du PDF");
    } finally {
      setPdfLoading(false);
      setTimeout(() => setShowPdfModal(false), 1000);
    }
  };

  const handleExportExcel = () => {
    try {
      exportCAtoExcel(data, selectedYear, selectedMonth);
    } catch (error) {
      console.error("Erreur Excel:", error);
      alert("Erreur lors de l'export Excel");
    }
  };

  const handleExportCSV = () => {
    try {
      exportCAtoCSV(data, selectedYear);
    } catch (error) {
      console.error("Erreur CSV:", error);
      alert("Erreur lors de l'export CSV");
    }
  };

  const fetchChiffreAffaires = async () => {
    try {
      setLoading(true);
      setChartLoading(true);
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${API_BASE_URL}/ca.php?annee=${selectedYear}&mois=${selectedMonth}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (!response.ok) throw new Error("Erreur de chargement des données");
      
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
      setChartLoading(false);
    }
  };

  const handleYearChange = (e) => {
    setSelectedYear(parseInt(e.target.value));
  };

  const handleMonthChange = (e) => {
    setSelectedMonth(parseInt(e.target.value));
  };

  const getMonthName = (month) => {
    const months = [
      'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
      'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
    ];
    return months[month - 1];
  };

  const caMensuel = data?.annuel?.find(m => m.mois === selectedMonth)?.total_ttc || 0;
  const caMensuelNettoye = nettoyerMontant(caMensuel);
  const caJournalier = data?.journalier?.total_ttc || 0;
  const nbFacturesJour = data?.journalier?.nb_factures || 0;
  const panierMoyen = nbFacturesJour > 0 ? caJournalier / nbFacturesJour : 0;
  
  const caAnnuel = data?.annuel?.reduce((acc, m) => {
    const montant = typeof m.total_ttc === 'number' 
        ? m.total_ttc 
        : parseFloat(String(m.total_ttc).replace(/[^\d.-]/g, '')) || 0;
    return acc + montant;
  }, 0) || 0;
  
  const tvaAnnuelle = data?.annuel?.reduce((acc, m) => acc + (m.total_tva || 0), 0) || 0;
  const regleAnnuel = data?.annuel?.reduce((acc, m) => acc + (m.total_regle || 0), 0) || 0;
  const tauxEncaissement = (caAnnuel && caAnnuel > 0) ? ((regleAnnuel || 0) / caAnnuel) * 100 : 0;

  const getChartOptions = (type) => {
    const baseOptions = {
      chart: {
        toolbar: { show: true, tools: { download: true } },
        zoom: { enabled: true },
        fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
        background: 'transparent'
      },
      grid: {
        borderColor: '#e9ecef',
        strokeDashArray: 5,
        position: 'back'
      },
      dataLabels: { enabled: false },
      colors: ['#4361ee', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6'],
      tooltip: {
        theme: 'dark',
        y: {
          formatter: (value) => formatCurrency(value),
          title: { formatter: (seriesName) => `${seriesName}: ` }
        },
        style: { fontSize: '12px' }
      },
      legend: {
        position: 'top',
        horizontalAlign: 'center',
        markers: { radius: 12 }
      }
    };

    if (type === 'mensuel') {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'line' },
        xaxis: {
          categories: Array.from({ length: 31 }, (_, i) => i + 1),
          title: { text: 'Jour du mois', style: { fontWeight: 500 } },
          labels: { rotate: -45 }
        },
        yaxis: {
          title: { text: 'Montant (FCFA)' },
          labels: { formatter: (value) => formatCurrency(value) }
        },
        stroke: { curve: 'smooth', width: 3, dashArray: 0 },
        fill: { type: 'gradient', gradient: { shadeIntensity: 0.5, opacityFrom: 0.7, opacityTo: 0.9 } }
      };
    }

    if (type === 'annuel') {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'bar' },
        xaxis: {
          categories: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
          title: { text: 'Mois' }
        },
        yaxis: { title: { text: 'Montant (FCFA)' }, labels: { formatter: (value) => formatCurrency(value) } },
        plotOptions: { bar: { columnWidth: '55%', borderRadius: 8, distributed: false } }
      };
    }

    if (type === 'evolution') {
      return {
        ...baseOptions,
        chart: { ...baseOptions.chart, type: 'area' },
        xaxis: { categories: data?.evolution?.map(item => item.annee) || [], title: { text: 'Année' } },
        yaxis: { title: { text: 'Montant (FCFA)' }, labels: { formatter: (value) => formatCurrency(value) } },
        fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.6, opacityTo: 0.1 } }
      };
    }

  if (type === 'repartition') {
  const modesMap = { 
    'especes': 'Espèces', 
    'cheque': 'Chèque', 
    'virement': 'Virement', 
    'carte_bancaire': 'Carte bancaire', 
    'traite': 'Traite'
  };
  
  return {
    chart: { 
      type: 'donut', 
      height: 320, 
      fontFamily: 'Inter, system-ui, sans-serif',
      toolbar: { show: false }
    },
    labels: data?.reglements?.map(item => modesMap[item.mode_reglement] || item.mode_reglement) || [],
    colors: ['#4361ee', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'],
    legend: { 
      position: 'bottom', 
      fontSize: '11px', 
      offsetY: 5,
      markers: { radius: 8 }
    },
    tooltip: { 
      y: { 
        formatter: (value) => formatCurrency(value)
      } 
    },
    plotOptions: {
      pie: {
        donut: {
          size: '65%',
          labels: {
            show: true,
            total: {
              show: true,
              label: 'Total',
              fontSize: '14px',
              fontWeight: 600,
              color: '#1a3c5e',
              formatter: () => formatCurrency(data?.reglements?.reduce((a,b) => a + b.total, 0) || 0)
            }
          }
        }
      }
    },
    dataLabels: { 
      style: { fontSize: '11px', fontWeight: 500 } 
    },
    stroke: { show: false }
  };
}

    return baseOptions;
  };

  const seriesMensuel = [{ name: 'CA Journalier', data: data?.mensuel_detail?.map(item => item.ca_journalier) || Array(31).fill(0) }];
  const seriesAnnuel = [{ name: 'CA Mensuel', data: data?.annuel?.map(item => item.total_ttc) || Array(12).fill(0) }];
  const seriesEvolution = [{ name: 'CA Annuel', data: data?.evolution?.map(item => item.total_ttc) || [] }];
  const seriesRepartition = data?.reglements?.map(item => item.total) || [];
  const seriesComparaison = [
    { name: `${selectedYear}`, data: data?.annuel?.map(item => item.total_ttc) || Array(12).fill(0) },
    { name: `${selectedYear - 1}`, data: data?.annee_precedente?.map(item => item.total_ttc) || Array(12).fill(0) }
  ];

  if (loading) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
          <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
            <div className="text-center">
              <div className="loading-animation mb-4">
                <div className="loading-ring">
                  <div className="loading-ring-inner"></div>
                </div>
                <i className="ri-bar-chart-2-line loading-icon"></i>
              </div>
              <h5 className="fw-bold text-primary mb-2">Chargement des données financières</h5>
              <p className="text-muted small mb-0">Analyse du chiffre d'affaires en cours...</p>
              <div className="mx-auto mt-4" style={{ width: '200px' }}>
                <div className="progress" style={{ height: '3px', borderRadius: '3px', background: '#e9ecef' }}>
                  <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: '100%', background: 'linear-gradient(90deg, #4361ee, #10b981, #f59e0b)' }} />
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
        <style>{`
          .loading-animation { position: relative; width: 80px; height: 80px; margin: 0 auto; }
          .loading-ring { position: absolute; top: 0; left: 0; width: 100%; height: 100%; border-radius: 50%; background: linear-gradient(135deg, #4361ee, #10b981); animation: pulse-ring 1.5s ease-in-out infinite; }
          .loading-ring-inner { position: absolute; top: 5px; left: 5px; right: 5px; bottom: 5px; border-radius: 50%; background: white; }
          .loading-icon { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); font-size: 28px; color: #4361ee; z-index: 2; animation: pulse-icon 1.5s ease-in-out infinite; }
          @keyframes pulse-ring { 0% { transform: scale(0.9); opacity: 0.7; } 50% { transform: scale(1.1); opacity: 0.3; } 100% { transform: scale(0.9); opacity: 0.7; } }
          @keyframes pulse-icon { 0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 1; } 50% { transform: translate(-50%, -50%) scale(1.1); opacity: 0.8; } }
        `}</style>
      </>
    );
  }

  if (error) {
    return (
      <>
        <Header />
        <div className="main main-app p-3 p-lg-4">
          <Alert variant="danger" className="border-0 shadow-sm rounded-0">
            <div className="d-flex align-items-center">
              <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
              <div>
                <h6 className="mb-1 fw-bold">Erreur de chargement</h6>
                <p className="mb-0 small">{error}</p>
                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => window.location.reload()}>
                  <i className="ri-refresh-line me-1"></i>Réessayer
                </Button>
              </div>
            </div>
          </Alert>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      
      <Modal show={showPdfModal} centered backdrop="static" keyboard={false} className="pdf-loading-modal">
        <Modal.Body className="text-center py-5">
          <div className="pdf-loader">
            <img src={logo} alt="Logo" className="pdf-loader-logo" />
            <div className="spinner-grow text-primary mt-3" role="status">
              <span className="visually-hidden">Chargement...</span>
            </div>
            <h5 className="mt-3 fw-bold text-success">Génération du PDF en cours...</h5>
            <p className="text-muted small">Veuillez patienter quelques instants</p>
            <div className="progress-bar-custom">
              <div className="progress-fill"></div>
            </div>
          </div>
        </Modal.Body>
      </Modal>

      <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
        
        {/* En-tête moderne */}
        <div className="d-md-flex align-items-center justify-content-between mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-2">
              <div className="p-2 bg-primary bg-opacity-10 rounded-0">
                <i className="ri-bar-chart-2-line fs-20 text-primary"></i>
              </div>
              <div>
                <h4 className="main-title mb-0 fw-bold">Analyse du Chiffre d'Affaires</h4>
                <p className="text-muted small mb-0">Suivez vos performances financières en temps réel</p>
              </div>
            </div>
          </div>
          
          <div className="d-flex gap-2 mt-3 mt-md-0">
            <Dropdown align="end">
              <Dropdown.Toggle variant="outline-secondary" className="d-flex align-items-center gap-2">
                <i className="ri-download-line fs-14 lh-1"></i> Exporter
              </Dropdown.Toggle>
              <Dropdown.Menu>
                <Dropdown.Item onClick={handleExportPDF} disabled={pdfLoading}>
                  <i className="ri-file-pdf-line text-danger me-2"></i> PDF
                </Dropdown.Item>
                <Dropdown.Item onClick={handleExportExcel}>
                  <i className="ri-file-excel-line text-success me-2"></i> Excel
                </Dropdown.Item>
                <Dropdown.Item onClick={handleExportCSV}>
                  <i className="ri-file-csv-line text-info me-2"></i> CSV
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        {/* Filtres */}
        <Card className="border-0 shadow-sm mb-4 rounded-0">
          <Card.Body className="py-3">
            <Row className="align-items-center g-3">
              <Col md="auto">
                <div className="d-flex align-items-center gap-2">
                  <i className="ri-filter-3-line text-muted"></i>
                  <span className="text-muted small fw-semibold">FILTRES :</span>
                </div>
              </Col>
              <Col md="auto">
                <Form.Select value={selectedYear} onChange={handleYearChange} className="rounded-pill px-3" style={{minWidth:'150px'}}>
                  {[2026, 2025, 2024, 2023, 2022].map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col md="auto">
                <Form.Select value={selectedMonth} onChange={handleMonthChange} className="rounded-pill px-3" style={{ minWidth:'150px' }}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                    <option key={month} value={month}>{getMonthName(month)}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col className="ms-auto">
                <div className="d-flex gap-2 justify-content-end">
                  {[
                    { mode: 'mensuel', label: 'Journalier', icon: 'ri-calendar-line' },
                    { mode: 'annuel', label: 'Mensuel', icon: 'ri-bar-chart-line' },
                    { mode: 'comparaison', label: 'Comparaison', icon: 'ri-git-compare-line' }
                  ].map(({ mode, label, icon }) => (
                    <Button
                      key={mode}
                      variant={viewMode === mode ? 'primary' : 'light'}
                      onClick={() => setViewMode(mode)}
                      size="sm"
                      className="d-flex align-items-center gap-2 rounded-pill px-3"
                    >
                      <i className={`${icon} fs-12`}></i>
                      <span>{label}</span>
                    </Button>
                  ))}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* KPI Cards modernes - Style StockCritique */}
        <Row className="g-3 mb-4">
          <Col sm="6" xl="3">
            <div className="kpi-card kpi-primary">
              <div className="kpi-icon">
                <i className="ri-money-euro-circle-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">CA du jour</span>
                <h3 className="kpi-value">{formatCurrency(caJournalier)}</h3>
                <span className="kpi-unit">{nbFacturesJour} factures</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-success">
              <div className="kpi-icon">
                <i className="ri-calendar-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">CA du mois</span>
                <h3 className="kpi-value">{formatCurrency(caMensuelNettoye)}</h3>
                <span className="kpi-unit">{getMonthName(selectedMonth)} {selectedYear}</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-warning">
              <div className="kpi-icon">
                <i className="ri-bar-chart-2-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">CA annuel</span>
                <h3 className="kpi-value">{formatCurrency(caAnnuel)}</h3>
                <span className="kpi-unit">Année {selectedYear}</span>
              </div>
            </div>
          </Col>

          <Col sm="6" xl="3">
            <div className="kpi-card kpi-info">
              <div className="kpi-icon">
                <i className="ri-line-chart-line fs-24"></i>
              </div>
              <div className="kpi-content">
                <span className="kpi-label">Taux d'encaissement</span>
                <h3 className="kpi-value">{isNaN(tauxEncaissement) ? '0.0' : tauxEncaissement.toFixed(1)}%</h3>
                <span className="kpi-unit">sur {formatCurrency(caAnnuel)}</span>
              </div>
            </div>
          </Col>
        </Row>

        {/* Graphiques */}
        <Row className="g-3 mb-4">
          <Col xl="8">
            <Card className="border-0 shadow-sm rounded-0">
              <Card.Header className="bg-transparent border-0 pt-3 px-4">
                <h6 className="mb-0 fw-semibold">
                  {viewMode === 'mensuel' && `CA Journalier - ${getMonthName(selectedMonth)} ${selectedYear}`}
                  {viewMode === 'annuel' && `CA Mensuel - Année ${selectedYear}`}
                  {viewMode === 'comparaison' && `Évolution du CA sur 5 ans`}
                </h6>
              </Card.Header>
              <Card.Body className="px-4 pb-4">
                <div style={{ position: 'relative', minHeight: '400px' }}>
                  {chartLoading ? (
                    <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : (
                    <>
                      {viewMode === 'mensuel' && <ReactApexChart series={seriesMensuel} options={getChartOptions('mensuel')} type="line" height={400} />}
                      {viewMode === 'annuel' && <ReactApexChart series={seriesAnnuel} options={getChartOptions('annuel')} type="bar" height={400} />}
                      {viewMode === 'comparaison' && <ReactApexChart series={seriesEvolution} options={getChartOptions('evolution')} type="area" height={400} />}
                    </>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl="4">
  <Card className="border-0 shadow-sm rounded-0 h-100">
    <Card.Header className="bg-transparent border-0 pt-3 px-4">
      <div className="d-flex align-items-center gap-2">
        <i className="ri-pie-chart-2-line text-success"></i>
        <h6 className="mb-0 fw-semibold">Répartition des règlements</h6>
      </div>
    </Card.Header>
    <Card.Body className="px-4 pb-4">
      {data?.reglements?.length > 0 ? (
        (() => {
          const modesMap = { 
            'especes': 'Espèces', 
            'cheque': 'Chèque', 
            'virement': 'Virement', 
            'carte_bancaire': 'Carte bancaire', 
            'traite': 'Traite'
          };
          
          // Garder toutes les valeurs mais s'assurer qu'elles sont positives
          let donutLabels = data.reglements.map(item => modesMap[item.mode_reglement] || item.mode_reglement);
          let donutSeries = data.reglements.map(item => Math.max(item.total, 0.01));
          
          // Optionnel: regrouper les très petites valeurs dans "Autres"
          const minPercentage = 5; // 5% minimum
          const total = donutSeries.reduce((a, b) => a + b, 0);
          
          if (total > 0) {
            const filteredData = [];
            const otherData = { label: 'Autres', value: 0 };
            
            donutSeries.forEach((value, index) => {
              const percentage = (value / total) * 100;
              if (percentage >= minPercentage) {
                filteredData.push({ label: donutLabels[index], value: value });
              } else {
                otherData.value += value;
              }
            });
            
            if (otherData.value > 0) {
              filteredData.push(otherData);
            }
            
            donutLabels = filteredData.map(item => item.label);
            donutSeries = filteredData.map(item => item.value);
          }
          
          console.log("Données du graphique:", { labels: donutLabels, series: donutSeries });
          
          const donutOptions = {
            chart: { 
              type: 'donut', 
              height: 320, 
              fontFamily: 'Inter, system-ui, sans-serif',
              toolbar: { show: false }
            },
            labels: donutLabels,
            colors: ['#4361ee', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'],
            legend: { 
              position: 'bottom', 
              fontSize: '11px', 
              offsetY: 5,
              markers: { radius: 8 }
            },
            tooltip: { 
              y: { 
                formatter: (value) => formatCurrency(value)
              } 
            },
            plotOptions: {
              pie: {
                donut: {
                  size: '65%',
                  labels: {
                    show: true,
                    total: {
                      show: true,
                      label: 'Total',
                      fontSize: '14px',
                      fontWeight: 600,
                      color: '#1a3c5e',
                      formatter: () => formatCurrency(donutSeries.reduce((a, b) => a + b, 0))
                    }
                  }
                }
              }
            },
            dataLabels: { 
              style: { fontSize: '11px', fontWeight: 500 } 
            },
            stroke: { show: false }
          };
          
          return (
            <ReactApexChart 
              series={donutSeries} 
              options={donutOptions} 
              type="donut" 
              height={320} 
            />
          );
        })()
      ) : (
        <div className="text-center py-5">
          <i className="ri-pie-chart-2-line fs-48 text-muted mb-3 d-block"></i>
          <p className="text-muted mb-0">Aucune donnée de règlement</p>
        </div>
      )}
    </Card.Body>
  </Card>
</Col>
        </Row>

        {/* Comparaison Annuelle */}
        {viewMode === 'comparaison' && (
          <Row className="mb-4">
            <Col xl="12">
              <Card className="border-0 shadow-sm rounded-0">
                <Card.Header className="bg-transparent border-0 pt-3 px-4">
                  <h6 className="mb-0 fw-semibold">Comparaison {selectedYear} vs {selectedYear - 1}</h6>
                </Card.Header>
                <Card.Body className="px-4 pb-4">
                  <div style={{ minHeight: '350px' }}>
                    {chartLoading ? (
                      <div className="d-flex justify-content-center align-items-center" style={{ height: '350px' }}>
                        <Spinner animation="border" variant="primary" />
                      </div>
                    ) : (
                      <ReactApexChart series={seriesComparaison} options={{
                        ...getChartOptions('annuel'),
                        colors: ['#4361ee', '#f59e0b']
                      }} type="line" height={350} />
                    )}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}

        {/* Tableaux détaillés */}
        <Row className="g-3 mb-4">
          <Col xl="8">
            <Card className="border-0 shadow-sm rounded-0 h-100">
              <Card.Header className="bg-transparent border-0 pt-3 px-4 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-semibold"> Détail mensuel {selectedYear}</h6>
                <Badge bg="light" text="dark" className="rounded-pill">12 mois</Badge>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Mois</th>
                        <th className="text-end">Factures</th>
                        <th className="text-end">Clients</th>
                        <th className="text-end">HT</th>
                        <th className="text-end">TVA</th>
                        <th className="text-end">TTC</th>
                        <th className="text-end pe-4">Réglé</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.annuel?.map((mois) => (
                        <tr key={mois.mois}>
                          <td className="ps-4 fw-semibold">{getMonthName(mois.mois)}</td>
                          <td className="text-end">{formatNumber(mois.nb_factures || 0)}</td>
                          <td className="text-end">{formatNumber(mois.nb_clients || 0)}</td>
                          <td className="text-end text-primary">{formatCurrency(mois.total_ht || 0)}</td>
                          <td className="text-end text-warning">{formatCurrency(mois.total_tva || 0)}</td>
                          <td className="text-end fw-bold">{formatCurrency(mois.total_ttc || 0)}</td>
                          <td className="text-end pe-4 text-success">{formatCurrency(mois.total_regle || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-light fw-bold">
                      <tr>
                        <td className="ps-4">TOTAL</td>
                        <td className="text-end">{formatNumber(data?.annuel?.reduce((acc, m) => acc + (m.nb_factures || 0), 0))}</td>
                        <td className="text-end">-</td>
                        <td className="text-end">{formatCurrency(data?.annuel?.reduce((acc, m) => acc + (m.total_ht || 0), 0))}</td>
                        <td className="text-end">{formatCurrency(data?.annuel?.reduce((acc, m) => acc + (m.total_tva || 0), 0))}</td>
                        <td className="text-end">{formatCurrency(caAnnuel)}</td>
                        <td className="text-end pe-4">{formatCurrency(regleAnnuel)}</td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          </Col>


            <Col xl="4">
            

            <Card className="border-0 shadow-sm rounded-0 h-100">
              <Card.Header className="bg-transparent border-0 pt-3 px-4">
                <h6 className="mb-0 fw-semibold"> Prévisions</h6>
              </Card.Header>
              <Card.Body className="p-4">
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">Mois prochain</span>
                    <span className="fw-bold text-primary">{formatCurrency(data?.previsions?.prevision_mois_prochain || 0)}</span>
                  </div>
                  <ProgressBar now={75} variant="primary" className="rounded-pill" style={{ height: 8 }} />
                </div>
                
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">Trimestre</span>
                    <span className="fw-bold text-success">{formatCurrency(data?.previsions?.prevision_trimestre || 0)}</span>
                  </div>
                  <ProgressBar now={85} variant="success" className="rounded-pill" style={{ height: 8 }} />
                </div>
                
                <div className="mb-4">
                  <div className="d-flex justify-content-between mb-2">
                    <span className="text-muted small">Année</span>
                    <span className="fw-bold text-warning">{formatCurrency(data?.previsions?.prevision_annuelle || 0)}</span>
                  </div>
                  <ProgressBar now={60} variant="warning" className="rounded-pill" style={{ height: 8 }} />
                </div>

                <hr className="my-3" />

                <div className="text-center">
                  <i className="ri-information-line text-muted me-1"></i>
                  <small className="text-muted">Prévisions basées sur la moyenne des 3 derniers mois</small>
                </div>
              </Card.Body>
            </Card>
          </Col>

          <Col xl="12">
            <Card className="border-0 shadow-sm rounded-0 mb-3">
              <Card.Header className="bg-transparent border-0 pt-3 px-4">
                <h6 className="mb-0 fw-semibold">🏆 Top 5 Clients</h6>
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="ps-4">Client</th>
                        <th className="text-end">CA</th>
                        <th className="text-end pe-4">Factures</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data?.top_clients?.slice(0,5).map((client) => (
                        <tr key={client.id_client}>
                          <td className="ps-4">
                            <div>
                              <span className="fw-semibold">{client.raison_sociale}</span>
                              <br />
                              <small className="text-muted">{client.code_client}</small>
                            </div>
                          </td>
                          <td className="text-end fw-semibold text-primary">{formatCurrency(client.total_ttc)}</td>
                          <td className="text-end pe-4"><Badge bg="info" className="rounded-pill">{client.nb_factures}</Badge></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>

            
          </Col>
        </Row>

        <Footer />
      </div>

      <style>{`
        /* KPI Cards */
        .kpi-card {
          background: white;
          border-radius: 0px;
          padding: 1rem;
          display: flex;
          align-items: center;
          gap: 1rem;
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          transition: all 0.2s ease;
          height: 100%;
          border-left: 2px solid;
        }
        .kpi-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(0,0,0,0.08);
        }
        .kpi-primary { border-left-color: #4361ee; }
        .kpi-success { border-left-color: #10b981; }
        .kpi-warning { border-left-color: #f59e0b; }
        .kpi-info { border-left-color: #0dcaf0; }
        
        .kpi-icon {
          width: 48px;
          height: 48px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .kpi-primary .kpi-icon { background: #e8f0fe; color: #4361ee; }
        .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
        .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
        .kpi-info .kpi-icon { background: #e2f3f5; color: #0dcaf0; }
        
        .kpi-content {
          flex: 1;
        }
        .kpi-label {
          font-size: 0.7rem;
          text-transform: uppercase;
          color: #6c757d;
          font-weight: 600;
          letter-spacing: 0.5px;
        }
        .kpi-value {
          font-size: 1.3rem;
          font-weight: 700;
          margin: 0.25rem 0;
          color: #1a3c5e;
        }
        .kpi-unit {
          font-size: 0.7rem;
          color: #6c757d;
        }
        
        .pdf-loading-modal .modal-content {
          background: #fff;
          border-radius: 20px;
        }
        .pdf-loader {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .pdf-loader-logo {
          width: 80px;
          height: 80px;
          object-fit: contain;
          border-radius: 50%;
          margin-bottom: 10px;
          animation: pulse 1.5s ease-in-out infinite;
        }
        @keyframes pulse {
          0% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
          100% { transform: scale(1); opacity: 1; }
        }
        .progress-bar-custom {
          width: 200px;
          height: 4px;
          background: rgba(0,0,0,0.1);
          border-radius: 4px;
          overflow: hidden;
          margin-top: 20px;
        }
        .progress-fill {
          width: 60%;
          height: 100%;
          background: #4361ee;
          border-radius: 4px;
          animation: loading 1s ease-in-out infinite;
        }
        @keyframes loading {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </>
  );
}